import React, { useEffect, useState } from 'react';
import { Modal } from '../../../components/organisms/Modal';
import { Button } from '../../../components/atoms';
import { FormField } from '../../../components/molecues/FormField';
import { resolveAssetUrl } from '../../../utils/url';
import {
  getArtistService,
  getAlbumService,
  getLanguageService,
} from '../../../service/ServiceRegistry';
import type {
  MusicVideo,
  MusicVideoAccessLevel,
  MusicVideoStatus,
  CreateMusicVideoRequest,
  UpdateMusicVideoRequest,
} from '../../../types/api/music-video';

interface MusicVideoFormState {
  title: string;
  description: string;
  artistId: string;
  albumId: string;
  languageId: string;
  durationSeconds: string;
  accessLevel: MusicVideoAccessLevel;
  status: MusicVideoStatus;
}

interface Option {
  id: string;
  label: string;
}

const defaultFormState: MusicVideoFormState = {
  title: '',
  description: '',
  artistId: '',
  albumId: '',
  languageId: '',
  durationSeconds: '',
  accessLevel: 'FREE',
  status: 'ACTIVE',
};

export interface MusicVideoFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    data: CreateMusicVideoRequest | UpdateMusicVideoRequest,
    thumbnailFile?: File | null,
    videoFile?: File | null
  ) => void | Promise<void>;
  mode: 'create' | 'edit';
  musicVideo?: MusicVideo;
}

export const MusicVideoFormModal: React.FC<MusicVideoFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  mode,
  musicVideo,
}) => {
  const [formData, setFormData] = useState<MusicVideoFormState>(defaultFormState);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoName, setVideoName] = useState<string | null>(null);
  const [artistOptions, setArtistOptions] = useState<Option[]>([]);
  const [albumOptions, setAlbumOptions] = useState<Option[]>([]);
  const [languageOptions, setLanguageOptions] = useState<Option[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    const loadOptions = async () => {
      try {
        const [artists, albums, languages] = await Promise.all([
          getArtistService().getArtists({ page: 1, size: 200 }),
          getAlbumService().getAlbums({ page: 1, size: 200 }),
          getLanguageService().getLanguages({ page: 1, size: 100 }),
        ]);
        if (cancelled) return;
        setArtistOptions(artists.items.map((a) => ({ id: a.artistId, label: a.name })));
        setAlbumOptions(albums.items.map((a) => ({ id: a.albumId, label: a.name })));
        setLanguageOptions(languages.items.map((l) => ({ id: l.languageId, label: l.name })));
      } catch {
        if (!cancelled) {
          setArtistOptions([]);
          setAlbumOptions([]);
          setLanguageOptions([]);
        }
      }
    };

    loadOptions();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (mode === 'edit' && musicVideo && isOpen) {
      setFormData({
        title: musicVideo.title || '',
        description: musicVideo.description || '',
        artistId: musicVideo.artistId || '',
        albumId: musicVideo.albumId || '',
        languageId: musicVideo.languageId || '',
        durationSeconds:
          musicVideo.durationSeconds !== undefined && musicVideo.durationSeconds !== null
            ? String(musicVideo.durationSeconds)
            : '',
        accessLevel: musicVideo.accessLevel || 'FREE',
        status: musicVideo.status || 'ACTIVE',
      });
      setThumbnailPreview(resolveAssetUrl(musicVideo.thumbnailUrl || null) || null);
      setThumbnailFile(null);
      setVideoFile(null);
      setVideoName(null);
    } else if (isOpen) {
      setFormData(defaultFormState);
      setThumbnailPreview(null);
      setThumbnailFile(null);
      setVideoFile(null);
      setVideoName(null);
    }
    setErrors({});
  }, [musicVideo, mode, isOpen]);

  const handleThumbnailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setErrors((prev) => ({ ...prev, thumbnail: 'Please select a valid image file' }));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, thumbnail: 'Thumbnail must be less than 10MB' }));
      return;
    }
    setErrors((prev) => {
      const next = { ...prev };
      delete next.thumbnail;
      return next;
    });
    const reader = new FileReader();
    reader.onloadend = () => {
      setThumbnailFile(file);
      setThumbnailPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleVideoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const isVideo =
      file.type.startsWith('video/') || /\.(mp4|webm)$/i.test(file.name);
    if (!isVideo) {
      setErrors((prev) => ({ ...prev, video: 'Please select an MP4 or WebM video file' }));
      return;
    }
    if (file.size > 500 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, video: 'Video must be less than 500MB' }));
      return;
    }
    setErrors((prev) => {
      const next = { ...prev };
      delete next.video;
      return next;
    });
    setVideoFile(file);
    setVideoName(file.name);
  };

  const validate = (): boolean => {
    const nextErrors: Record<string, string> = {};
    if (!formData.title.trim()) {
      nextErrors.title = 'Title is required';
    } else if (formData.title.trim().length > 255) {
      nextErrors.title = 'Title must be less than 255 characters';
    }
    if (!formData.artistId) {
      nextErrors.artistId = 'Artist is required';
    }
    if (formData.durationSeconds.trim() !== '') {
      const n = Number(formData.durationSeconds);
      if (!Number.isInteger(n) || n < 0) {
        nextErrors.durationSeconds = 'Duration must be a whole number of seconds';
      }
    }
    if (formData.description.length > 5000) {
      nextErrors.description = 'Description must be less than 5000 characters';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validate()) return;

    const base = {
      title: formData.title.trim(),
      description: formData.description.trim() || undefined,
      artistId: formData.artistId,
      albumId: formData.albumId || undefined,
      languageId: formData.languageId || undefined,
      durationSeconds:
        formData.durationSeconds.trim() !== '' ? Number(formData.durationSeconds) : undefined,
      accessLevel: formData.accessLevel,
      status: formData.status,
    };

    const payload: CreateMusicVideoRequest | UpdateMusicVideoRequest =
      mode === 'create' ? (base as CreateMusicVideoRequest) : (base as UpdateMusicVideoRequest);

    setIsSubmitting(true);
    try {
      await onSubmit(payload, thumbnailFile, videoFile);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={mode === 'create' ? 'Add Music Video' : 'Edit Music Video'}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} isLoading={isSubmitting} type="submit">
            {mode === 'create' ? 'Create' : 'Update'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormField
          label="Title"
          value={formData.title}
          onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
          error={errors.title}
          required
          placeholder="e.g., Music Video Title"
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Description</label>
          <textarea
            rows={3}
            value={formData.description}
            onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
            placeholder="Optional description"
          />
          {errors.description ? (
            <p className="text-red-500 text-xs mt-1">{errors.description}</p>
          ) : null}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
            Artist <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.artistId}
            onChange={(e) => setFormData((prev) => ({ ...prev, artistId: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
          >
            <option value="">Select an artist</option>
            {artistOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
          {errors.artistId ? <p className="text-red-500 text-xs mt-1">{errors.artistId}</p> : null}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Album</label>
            <select
              value={formData.albumId}
              onChange={(e) => setFormData((prev) => ({ ...prev, albumId: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
            >
              <option value="">No album</option>
              {albumOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Language</label>
            <select
              value={formData.languageId}
              onChange={(e) => setFormData((prev) => ({ ...prev, languageId: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
            >
              <option value="">No language</option>
              {languageOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <FormField
          label="Duration (seconds)"
          type="number"
          value={formData.durationSeconds}
          onChange={(e) => setFormData((prev) => ({ ...prev, durationSeconds: e.target.value }))}
          error={errors.durationSeconds}
          min={0}
          step={1}
          placeholder="e.g., 240"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Access Level</label>
            <select
              value={formData.accessLevel}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  accessLevel: e.target.value as MusicVideoAccessLevel,
                }))
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
            >
              <option value="FREE">Free</option>
              <option value="PREMIUM">Premium</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Status</label>
            <select
              value={formData.status}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, status: e.target.value as MusicVideoStatus }))
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
            >
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
              <option value="PENDING">Pending</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5 text-left">Thumbnail</label>
          <div className="w-28 h-28 rounded-lg bg-gray-100 border flex items-center justify-center overflow-hidden mb-2">
            {thumbnailPreview ? (
              <img src={thumbnailPreview} alt="Thumbnail preview" className="w-full h-full object-cover" />
            ) : (
              <span className="text-xs text-gray-400">No thumbnail</span>
            )}
          </div>
          <input
            type="file"
            accept="image/*"
            onChange={handleThumbnailChange}
            className="block text-sm text-gray-700"
          />
          {mode === 'edit' ? (
            <p className="text-xs text-gray-500 mt-1">Leave empty to keep the current thumbnail.</p>
          ) : null}
          {errors.thumbnail ? <p className="text-sm text-red-600 mt-1">{errors.thumbnail}</p> : null}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5 text-left">Video File</label>
          <input
            type="file"
            accept="video/mp4,video/webm,.mp4,.webm"
            onChange={handleVideoChange}
            className="block text-sm text-gray-700"
          />
          {videoName ? (
            <p className="text-xs text-gray-500 mt-1">Selected: {videoName}</p>
          ) : mode === 'edit' ? (
            <p className="text-xs text-gray-500 mt-1">Leave empty to keep the current video.</p>
          ) : null}
          {errors.video ? <p className="text-sm text-red-600 mt-1">{errors.video}</p> : null}
        </div>
      </form>
    </Modal>
  );
};

export default MusicVideoFormModal;
